import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API
    from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";


/*
|--------------------------------------------------------------------------
| IMPORTANT
|--------------------------------------------------------------------------
|
| Platform token, NOT tenant token.
|--------------------------------------------------------------------------
*/

const platformApi =
    new API({
        tokenKey:
            "platformAccessToken",
    });


const initialState = {

    settings:
        null,

    loading:
        false,

    updating:
        false,

    error:
        null,
};


/*
|--------------------------------------------------------------------------
| FETCH
|--------------------------------------------------------------------------
*/

export const fetchPlatformSettings =
    createAsyncThunk(
        "platformSettings/fetch",

        async (
            _,
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.get(
                    config.platformSettings
                );


            if (
                !response?.success
            ) {

                return rejectWithValue({
                    message:
                        response?.message ||
                        "Unable to load platform settings.",
                });

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| UPDATE
|--------------------------------------------------------------------------
*/

export const updatePlatformSettings =
    createAsyncThunk(
        "platformSettings/update",

        async (
            payload,
            {
                rejectWithValue,
            }
        ) => {

            const response =
                await platformApi.put(
                    config.platformSettings,
                    payload
                );


            if (
                !response?.success
            ) {

                return rejectWithValue({
                    message:
                        response?.message ||
                        "Unable to update platform settings.",
                });

            }


            return response;

        }
    );


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const platformSettingsSlice =
    createSlice({

        name:
            "platformSettings",

        initialState,

        reducers: {

            clearPlatformSettingsError:
                (
                    state
                ) => {

                    state.error =
                        null;

                },

        },

        extraReducers:
            (
                builder
            ) => {

                builder

                    /*
                    |--------------------------------------------------------------------------
                    | FETCH
                    |--------------------------------------------------------------------------
                    */

                    .addCase(
                        fetchPlatformSettings.pending,

                        (
                            state
                        ) => {

                            state.loading =
                                true;

                            state.error =
                                null;

                        }
                    )

                    .addCase(
                        fetchPlatformSettings.fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.loading =
                                false;

                            state.settings =
                                action
                                    .payload
                                    ?.data ||
                                null;

                        }
                    )

                    .addCase(
                        fetchPlatformSettings.rejected,

                        (
                            state,
                            action
                        ) => {

                            state.loading =
                                false;

                            state.error =
                                action.payload ||
                                {
                                    message:
                                        "Unable to load settings.",
                                };

                        }
                    )


                    /*
                    |--------------------------------------------------------------------------
                    | UPDATE
                    |--------------------------------------------------------------------------
                    */

                    .addCase(
                        updatePlatformSettings.pending,

                        (
                            state
                        ) => {

                            state.updating =
                                true;

                            state.error =
                                null;

                        }
                    )

                    .addCase(
                        updatePlatformSettings.fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.updating =
                                false;

                            state.settings =
                                action
                                    .payload
                                    ?.data ||
                                state.settings;

                        }
                    )

                    .addCase(
                        updatePlatformSettings.rejected,

                        (
                            state,
                            action
                        ) => {

                            state.updating =
                                false;

                            state.error =
                                action.payload ||
                                {
                                    message:
                                        "Unable to update settings.",
                                };

                        }
                    );

            },

    });


export const {
    clearPlatformSettingsError,
} =
    platformSettingsSlice.actions;


export const selectPlatformSettings =
    (
        state
    ) =>
        state
            .platformSettings
            ?.settings ||
        null;


export const selectPlatformSettingsLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformSettings
                ?.loading
        );


export const selectPlatformSettingsUpdating =
    (
        state
    ) =>
        Boolean(
            state
                .platformSettings
                ?.updating
        );


export const selectPlatformSettingsError =
    (
        state
    ) =>
        state
            .platformSettings
            ?.error ||
        null;


export default
    platformSettingsSlice.reducer;